import React from 'react';
import Styled from 'styled';
import { ApolloConsumer } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import debounce from 'lodash/debounce';
import Promise from 'bluebird';
import { ApolloClient } from '@apollo/client';

import SVGIcon from 'lib/images/svg-icon/svg-icon';

import plus from 'four-nets/icons/svg/icon_plus.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import RecommendedUser from './recommended-user/recommended-user';
import query, { GroupAddPanelQuery, GroupAddPanelQueryVariables } from './group-add-panel.graphql';
import { GroupPrivacy } from '../group-loader.graphql.types';
import defaultStyles from './group-add-panel.scss';

export interface Props {
    styles: typeof defaultStyles;
    groupId: string;
    privacyType: GroupPrivacy;
}

interface State {
    value: string;
    loading?: boolean;
    data?: GroupAddPanelQuery;
}

const GROUP_SEARCH_MINIMUM_LENGTH = 4;

class GroupAddPanel extends React.PureComponent<Props, State> {
    state: State = { value: '' };
    client?: ApolloClient<any>;
    promise?: Promise<void>;

    componentDidMount() {
        setTimeout(() => this.searchHandler(''), 0);
    }

    searchHandler = (username: string) => {
        const { groupId } = this.props;

        if (this.promise && this.promise.isPending()) {
            this.promise.cancel();
        }
        this.setState({ loading: true });

        const promise = this.client!.query<GroupAddPanelQuery, GroupAddPanelQueryVariables>({
            query,
            variables: { groupId, username },
        })
            .then(({ data }) => {
                this.setState({ data, loading: false });
            })
            .finally(() => {
                this.setState({ loading: false });
                this.promise = undefined;
            });

        this.promise = promise as any;
    };

    onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ value: event.target.value });
        if (event.target.value.length >= GROUP_SEARCH_MINIMUM_LENGTH) {
            this.search(event.target.value);
        }
    };

    // tslint:disable-next-line: member-ordering
    search = debounce(this.searchHandler, 500);

    componentWillUnmount() {
        if (this.promise && this.promise.isPending()) {
            this.promise.cancel();
        }
    }

    render() {
        const { styles, groupId, privacyType } = this.props;
        const { data } = this.state;

        return (
            <ApolloConsumer>
                {client => {
                    this.client = client;

                    return (
                        <div className={styles.wrapper}>
                            <div className={styles.title}>
                                {privacyType === GroupPrivacy.GROUP_PRIVACY_PRIVATE ? (
                                    <FormattedMessage id='ADD_FRIEND_TO_GROUP' />
                                ) : (
                                    <FormattedMessage id='INVITE_FRIEND_TO_GROUP' />
                                )}
                            </div>
                            <div className={styles.container}>
                                <div className={styles.inputWrapper}>
                                    <SVGIcon style={{ image: styles.inputIcon }} src={plus} />
                                    <input className={styles.input} value={this.state.value} onChange={this.onChange} />
                                </div>

                                <ul className={styles.recommendedUsers}>
                                    <LoadingWrapperView loading={this.state.loading}>
                                        {!this.state.loading &&
                                        this.state.value &&
                                        this.state.value.length < GROUP_SEARCH_MINIMUM_LENGTH ? (
                                            <FormattedMessage
                                                id='GROUP_SEARCH_MINIMUM_LENGTH'
                                                values={{ count: GROUP_SEARCH_MINIMUM_LENGTH }}
                                            >
                                                {msg => <div className={styles.info}>{msg}</div>}
                                            </FormattedMessage>
                                        ) : null}
                                        {data &&
                                        data.groupInviteRecommendations &&
                                        data.groupInviteRecommendations.edges ? (
                                            data.groupInviteRecommendations.edges.length ? (
                                                data.groupInviteRecommendations.edges.map(edge =>
                                                    edge && edge.node ? (
                                                        <RecommendedUser
                                                            key={edge.node.id}
                                                            {...edge.node}
                                                            groupId={groupId}
                                                            privacyType={privacyType}
                                                            refetch={() => {
                                                                this.searchHandler(this.state.value);
                                                            }}
                                                        />
                                                    ) : null
                                                )
                                            ) : (
                                                <FormattedMessage id='NO_RESULTS'>
                                                    {msg => <div className={styles.info}>{msg}</div>}
                                                </FormattedMessage>
                                            )
                                        ) : null}
                                    </LoadingWrapperView>
                                </ul>
                            </div>
                        </div>
                    );
                }}
            </ApolloConsumer>
        );
    }
}

export default Styled(defaultStyles)(GroupAddPanel);
